// src/utils/enrollment.js
//
// ===========================================================================
// ENROLMENT IS NOT ENTITLEMENT
// ===========================================================================
// Two different questions, two different documents, and conflating them is
// the bug this file exists to prevent:
//
//   ENROLMENT   users/{uid}.courses         "what am I taking this year?"
//   ENTITLEMENT subscriptions/{uid}.courses "what have I paid to unlock?"
//
// A 10th grader taking Geometry, Biology I and World History II is enrolled
// in three courses from the moment they register — before any money moves.
// That enrolment is what decides which subject buttons they see. What they've
// paid for decides how many topics open inside each one.
//
// Keeping them apart is what lets someone sign up free, browse six topics of
// Geometry, and later pay for just that one course. Merging them would mean
// either "you can't see it until you buy it" (no free tier) or "enrolling is
// buying" (no browsing).
//
// ===========================================================================
// THE SHAPE ENFORCES THE RULE
// ===========================================================================
//     users/{uid}.courses = {
//       math:           "geometry",
//       science:        "biology-1",
//       socialstudies:  "world-history-2",
//       other:          "10th",
//     }
//
// A MAP KEYED BY SUBJECT, not a list of courses. A student takes one maths
// class — you cannot be in Geometry and Algebra 2 in the same year — and this
// shape makes that structurally true rather than a validation rule someone
// has to remember to call. Writing a second maths course overwrites the
// first, because there is nowhere else for it to go.
//
// A list would have needed a check on every write path: registration, the
// add-subjects flow, the admin editor, the seat claim. Four places to get it
// right, and the failure mode is a student billed for two maths courses.

import { SUBJECTS } from "../data/subjects";
import { resolveTrack, courseByToken, ENROLLMENT } from "../data/courses";
import { gradeBand, BAND, PLANS } from "../config/plans";

// Subjects that cost money. "other" — art, music, health, PE — is deliberately
// absent: it's included with any plan and with none, so it never enters the
// count and never appears on an invoice.
export const PAID_SUBJECTS = ["math", "science", "socialstudies"];

export const FREE_SUBJECT = "other";

// Every subject a secondary student has, paid or not. Order follows
// data/subjects.js so the nav and the plan page can't disagree.
export const ALL_SUBJECTS = SUBJECTS.map((s) => s.value);

export const isPaidSubject = (subject) => PAID_SUBJECTS.includes(subject);

// ---------------------------------------------------------------------------
// Reading enrolment
// ---------------------------------------------------------------------------

/**
 * The enrolment map, normalised.
 *
 * Elementary accounts return {} — every K-5 subject is addressed by grade, so
 * there is nothing to enrol in and nothing to choose. Callers can treat an
 * empty map as "show everything", which is exactly right for K-5.
 */
export function enrolledCourses(profile = {}) {
  if (gradeBand(profile.grade) !== BAND.SECONDARY) return {};

  const raw = profile.courses || {};
  const out = {};
  ALL_SUBJECTS.forEach((subject) => {
    const token = raw[subject];
    if (token) out[subject] = String(token).toLowerCase();
  });
  return out;
}

/**
 * The subjects whose buttons this person should see.
 *
 * A 10th grader in World History II sees Social Studies and nothing else
 * until they add more. Showing all four would be showing three doors that
 * open onto an empty room — they aren't taking Chemistry, so there is no
 * 10th-grade chemistry page that means anything to them.
 *
 * "other" is always included: it costs nothing and every student has art,
 * music or PE somewhere in their week.
 */
export function visibleSubjects(profile = {}) {
  if (gradeBand(profile.grade) !== BAND.SECONDARY) return ALL_SUBJECTS;

  const enrolled = enrolledCourses(profile);
  const chosen = ALL_SUBJECTS.filter((s) => enrolled[s]);

  // Nobody has chosen yet — an account created before this existed, or a seat
  // claimed without a schedule. Show everything rather than an empty nav; a
  // page with no buttons looks broken, and the add-subjects flow is a better
  // place to ask than a dead end.
  if (chosen.length === 0) return ALL_SUBJECTS;

  return chosen.includes(FREE_SUBJECT) ? chosen : [...chosen, FREE_SUBJECT];
}

/**
 * The track token for a subject — course token for an enrolled course, grade
 * token otherwise. This is what goes in the :grade slot of the portal URL.
 */
export function trackForSubject(profile = {}, subject) {
  const enrolled = enrolledCourses(profile);
  if (enrolled[subject]) return enrolled[subject];

  const resolved = resolveTrack({
    state: profile.state,
    grade: profile.grade,
    subject,
  });
  return resolved.track || "";
}

// ---------------------------------------------------------------------------
// Writing enrolment
// ---------------------------------------------------------------------------

/**
 * Set the course for one subject, replacing whatever was there.
 *
 * Returns a NEW map — never mutates. The caller writes it with a merge, and
 * because the subject key is overwritten wholesale there is no way to end up
 * with two courses in one subject.
 */
export function setCourse(courses = {}, subject, token) {
  const next = { ...courses };
  if (!token) delete next[subject];
  else next[subject] = String(token).toLowerCase();
  return next;
}

/**
 * The courses that cost money, as track tokens. This is the list sent to
 * createCheckoutSession and written onto subscriptions/{uid}.courses.
 */
export function billableCourses(courses = {}) {
  return PAID_SUBJECTS.map((s) => courses[s]).filter(Boolean);
}

export function billableCount(courses = {}) {
  return billableCourses(courses).length;
}

// ---------------------------------------------------------------------------
// Pricing
// ---------------------------------------------------------------------------
// Per-subject up to the point where the bundle is cheaper, then the bundle.
//
// Because a student takes at most one maths, one science and one social
// studies, THREE IS THE CEILING. So the "all subjects" price isn't an
// unbounded upsell — it is precisely the three-subject price, and it caps
// what anyone can be charged:
//
//     1 subject   $4.99
//     2 subjects  $9.98
//     3 subjects  $12.99   ← not $14.97
//
// The cap needs no new Stripe Price. It's a choice between two Prices that
// already exist: quantity-2 on the per-subject one, or the flat bundle. See
// bestPlanFor below.

const MONTHLY_UNIT = 4.99;
const YEARLY_UNIT = 49.9;

function unitPrice(billing) {
  return billing === "yearly" ? YEARLY_UNIT : MONTHLY_UNIT;
}

/**
 * The bundle price for a role, read from PLANS rather than hardcoded.
 *
 * Parsed out of `priceLabel` deliberately: that string is what the customer
 * is shown, so deriving the cap from it means the number they're quoted and
 * the number they're capped at cannot drift apart. A second constant here
 * could disagree with the label and nothing would catch it.
 */
export function bundlePrice(role, billing) {
  const plan = PLANS[`${role}_all_${billing}`];
  const match = String(plan?.priceLabel || "").match(/\$([\d,.]+)/);
  return match ? Number.parseFloat(match[1].replace(/,/g, "")) : null;
}

/**
 * What this selection costs, with the cap applied.
 *
 * @returns {{ total: number, capped: boolean, unitTotal: number, bundle: number|null }}
 */
export function priceFor({ role, billing = "monthly", count }) {
  const unitTotal = Number((count * unitPrice(billing)).toFixed(2));
  const bundle = bundlePrice(role, billing);

  if (bundle == null || unitTotal <= bundle) {
    return { total: unitTotal, capped: false, unitTotal, bundle };
  }
  return { total: bundle, capped: true, unitTotal, bundle };
}

/**
 * Which existing plan to actually buy, given how many subjects were picked.
 *
 * NO NEW STRIPE PRICES. At one or two subjects the per-subject Price with
 * quantity 1 or 2 is cheaper; at three the bundle is. So the "cap" is really
 * just picking whichever of the two already-configured Prices costs less —
 * which is also why the customer gets the better deal automatically instead
 * of having to notice it themselves.
 *
 * @returns {{ planKey: string|null, courses: string[], quantity: number }}
 */
export function bestPlanFor({ role, billing = "monthly", courses = {} }) {
  const billable = billableCourses(courses);
  const count = billable.length;

  if (count === 0) return { planKey: null, courses: [], quantity: 0 };

  const { capped } = priceFor({ role, billing, count });

  if (capped) {
    // The bundle. `courses` is empty on purpose — an all-scoped subscription
    // must not carry a course list, or a later subject added to their
    // schedule would read as unpaid. See subscriptionCoversCourse().
    return { planKey: `${role}_all_${billing}`, courses: [], quantity: 1 };
  }

  return {
    planKey: `${role}_subject_${billing}`,
    courses: billable,
    quantity: count,
  };
}

// ---------------------------------------------------------------------------
// Choices
// ---------------------------------------------------------------------------

/**
 * The options for one subject, for a picker.
 *
 * A subject with no course split in this state (or "other" anywhere) yields a
 * single option using the grade token — the same token the portal URL uses,
 * so nothing has to translate it later.
 */
export function optionsForSubject({ state, grade, subject }) {
  const resolved = resolveTrack({ state, grade, subject });

  if (resolved.mode === ENROLLMENT.FIXED) {
    const label =
      resolved.course?.title ||
      `${SUBJECTS.find((s) => s.value === subject)?.label || subject} ${grade}`;
    return [{ token: resolved.track, label, fixed: true }];
  }

  return resolved.options.map((c) => ({
    token: c.token,
    label: c.title,
    fixed: false,
  }));
}

/**
 * Human label for an enrolled track token.
 */
export function courseLabel({ state, subject, token, grade }) {
  const course = courseByToken(state, subject, token);
  if (course) return course.title;
  const label = SUBJECTS.find((s) => s.value === subject)?.label || subject;
  return grade ? `${label} ${grade}` : label;
}

/**
 * Subjects with a course still to pick — what "Add more subjects" offers.
 */
export function availableToAdd(profile = {}) {
  const enrolled = enrolledCourses(profile);
  return PAID_SUBJECTS.filter((s) => !enrolled[s]);
}
